import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Minus, DollarSign, Settings, X, Check, Edit2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { oilFieldPipingDatabase } from '../data/materials';
import { toast } from 'sonner';

const PipingCalculator = ({ pipingMaterials, setPipingMaterials }) => {
  const [selectedPipe, setSelectedPipe] = useState('');
  const [linearFeet, setLinearFeet] = useState('');
  const [costPerTon, setCostPerTon] = useState('');
  
  // Custom pipe management
  const [customPipes, setCustomPipes] = useState(() => {
    const saved = localStorage.getItem('customPipeTypes');
    return saved ? JSON.parse(saved) : [];
  });
  const [showManagePipes, setShowManagePipes] = useState(false);
  const [showAddPipe, setShowAddPipe] = useState(false);
  const [newPipe, setNewPipe] = useState({
    name: '',
    weightPerLinearFt: '',
    fluidCapacityGallons: '',
    costPerTon: ''
  });
  const [editingPipe, setEditingPipe] = useState(null);

  // All pipes (database + custom)
  const allPipes = [...oilFieldPipingDatabase, ...customPipes.map(p => ({
    ...p,
    category: 'Custom',
    totalWeightPerFt: parseFloat(p.weightPerLinearFt) + (parseFloat(p.fluidCapacityGallons) * 7), // crude oil ~7 lbs/gal
    description: `Custom pipe: ${p.name}`
  }))];

  // Save custom pipes to localStorage
  useEffect(() => {
    localStorage.setItem('customPipeTypes', JSON.stringify(customPipes));
  }, [customPipes]);

  const handleAddPipe = () => {
    if (!selectedPipe || !linearFeet || linearFeet <= 0) {
      toast.error('Please select a pipe and enter linear feet');
      return;
    }

    const pipeData = allPipes.find(p => p.name === selectedPipe);
    if (!pipeData) return;

    const linearFt = parseFloat(linearFeet);
    const totalWeight = (pipeData.totalWeightPerFt || pipeData.weightPerLinearFt) * linearFt;
    const totalFluidGallons = (pipeData.fluidCapacityGallons || 0) * linearFt;
    const totalTons = totalWeight / 2000;
    const cost = parseFloat(costPerTon) || pipeData.costPerTon || 0;
    const totalCost = totalTons * cost;

    const newPipeEntry = {
      id: Date.now(),
      name: pipeData.name,
      category: pipeData.category || 'Custom',
      linearFeet: linearFt,
      weightPerFt: pipeData.totalWeightPerFt || pipeData.weightPerLinearFt,
      fluidCapacityGallons: pipeData.fluidCapacityGallons || 0,
      totalWeight: totalWeight,
      totalTons: totalTons,
      totalFluidGallons: totalFluidGallons,
      costPerTon: cost,
      totalCost: totalCost,
      addedAt: new Date().toISOString()
    };

    setPipingMaterials([...pipingMaterials, newPipeEntry]);
    toast.success(`Added ${linearFt} ft of ${pipeData.name}`);
    
    // Reset form
    setSelectedPipe('');
    setLinearFeet('');
    setCostPerTon('');
  };

  const handleRemove = (id) => {
    setPipingMaterials(pipingMaterials.filter(pipe => pipe.id !== id));
    toast.success('Pipe removed');
  };

  // Custom pipe management
  const handleAddCustomPipe = () => {
    if (!newPipe.name || !newPipe.weightPerLinearFt) {
      toast.error('Please enter pipe name and weight per foot');
      return;
    }

    const customPipe = {
      id: Date.now(),
      name: newPipe.name.trim(),
      weightPerLinearFt: parseFloat(newPipe.weightPerLinearFt),
      fluidCapacityGallons: parseFloat(newPipe.fluidCapacityGallons) || 0,
      costPerTon: parseFloat(newPipe.costPerTon) || 0
    };

    setCustomPipes([...customPipes, customPipe]);
    setNewPipe({ name: '', weightPerLinearFt: '', fluidCapacityGallons: '', costPerTon: '' });
    setShowAddPipe(false);
    toast.success(`Custom pipe "${customPipe.name}" added`);
  };

  const handleRemoveCustomPipe = (id) => {
    setCustomPipes(customPipes.filter(p => p.id !== id));
    toast.success('Custom pipe removed');
  };

  const handleUpdateCustomPipe = (id) => {
    if (!editingPipe.name || !editingPipe.weightPerLinearFt) {
      toast.error('Please enter pipe name and weight per foot');
      return;
    }
    setCustomPipes(customPipes.map(p => p.id === id ? { ...editingPipe } : p));
    setEditingPipe(null);
    toast.success('Custom pipe updated');
  };

  const totals = pipingMaterials.reduce((acc, pipe) => ({
    totalLinearFeet: acc.totalLinearFeet + pipe.linearFeet,
    totalWeight: acc.totalWeight + pipe.totalWeight,
    totalTons: acc.totalTons + (pipe.totalTons || pipe.totalWeight / 2000),
    totalFluidGallons: acc.totalFluidGallons + (pipe.totalFluidGallons || 0),
    totalCost: acc.totalCost + (pipe.totalCost || 0)
  }), { totalLinearFeet: 0, totalWeight: 0, totalTons: 0, totalFluidGallons: 0, totalCost: 0 });

  return (
    <div className="space-y-6">
      {/* Manage Custom Pipes */}
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Manage Custom Pipe Types
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowManagePipes(!showManagePipes)}
            >
              {showManagePipes ? 'Hide' : 'Manage'}
            </Button>
          </CardTitle>
        </CardHeader>
        {showManagePipes && (
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Add custom pipe types with specific weights and costs.
            </p>

            {/* Add New Pipe Form */}
            {showAddPipe ? (
              <div className="grid grid-cols-5 gap-2 p-3 bg-muted/30 rounded-lg">
                <Input
                  placeholder="Pipe name"
                  value={newPipe.name}
                  onChange={(e) => setNewPipe({...newPipe, name: e.target.value})}
                  className="h-9"
                />
                <Input
                  type="number"
                  placeholder="Weight/ft (lbs)"
                  value={newPipe.weightPerLinearFt}
                  onChange={(e) => setNewPipe({...newPipe, weightPerLinearFt: e.target.value})}
                  className="h-9"
                />
                <Input
                  type="number"
                  placeholder="Fluid gal/ft"
                  value={newPipe.fluidCapacityGallons}
                  onChange={(e) => setNewPipe({...newPipe, fluidCapacityGallons: e.target.value})}
                  className="h-9"
                />
                <Input
                  type="number"
                  placeholder="Cost/ton"
                  value={newPipe.costPerTon}
                  onChange={(e) => setNewPipe({...newPipe, costPerTon: e.target.value})}
                  className="h-9"
                />
                <div className="flex gap-1">
                  <Button size="sm" onClick={handleAddCustomPipe} className="h-9 px-3">
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowAddPipe(false)} className="h-9 px-3">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setShowAddPipe(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Add Custom Pipe
              </Button>
            )}

            {/* Custom Pipes List */}
            {customPipes.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Custom Pipes ({customPipes.length})</Label>
                {customPipes.map(pipe => (
                  <div key={pipe.id} className="flex items-center justify-between p-2 bg-background rounded border">
                    {editingPipe?.id === pipe.id ? (
                      <div className="flex-1 grid grid-cols-5 gap-2">
                        <Input
                          value={editingPipe.name}
                          onChange={(e) => setEditingPipe({...editingPipe, name: e.target.value})}
                          className="h-8"
                        />
                        <Input
                          type="number"
                          value={editingPipe.weightPerLinearFt}
                          onChange={(e) => setEditingPipe({...editingPipe, weightPerLinearFt: e.target.value})}
                          className="h-8"
                        />
                        <Input
                          type="number"
                          value={editingPipe.fluidCapacityGallons}
                          onChange={(e) => setEditingPipe({...editingPipe, fluidCapacityGallons: e.target.value})}
                          className="h-8"
                        />
                        <Input
                          type="number"
                          value={editingPipe.costPerTon}
                          onChange={(e) => setEditingPipe({...editingPipe, costPerTon: e.target.value})}
                          className="h-8"
                        />
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => handleUpdateCustomPipe(pipe.id)} className="h-8 px-2">
                            <Check className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingPipe(null)} className="h-8 px-2">
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1">
                          <span className="font-medium">{pipe.name}</span>
                          <span className="text-sm text-muted-foreground ml-2">
                            ({pipe.weightPerLinearFt} lbs/ft, {pipe.fluidCapacityGallons || 0} gal/ft)
                          </span>
                          {pipe.costPerTon > 0 && (
                            <Badge variant="secondary" className="ml-2">${pipe.costPerTon}/ton</Badge>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditingPipe(pipe)} className="h-7 px-2">
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleRemoveCustomPipe(pipe.id)} className="h-7 px-2 text-destructive">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Add Pipe Form */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            Add Oil Field Piping
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs">
                  <p className="text-sm">Calculate oil field piping costs by selecting pipe type and entering linear feet. Includes weight calculations based on pipe specifications and fluid capacity.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="pipe-select">Pipe Type</Label>
            <Select value={selectedPipe} onValueChange={setSelectedPipe}>
              <SelectTrigger id="pipe-select">
                <SelectValue placeholder="Select pipe type..." />
              </SelectTrigger>
              <SelectContent>
                {/* Database Pipes */}
                {oilFieldPipingDatabase.map((pipe) => (
                  <SelectItem key={pipe.name} value={pipe.name}>
                    {pipe.name} - {pipe.totalWeightPerFt.toFixed(2)} lbs/ft
                  </SelectItem>
                ))}
                {/* Custom Pipes */}
                {customPipes.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-muted/50">Custom Pipes</div>
                    {customPipes.map((pipe) => (
                      <SelectItem key={`custom-${pipe.id}`} value={pipe.name}>
                        {pipe.name} - {pipe.weightPerLinearFt} lbs/ft (Custom)
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="linear-feet">Linear Feet</Label>
              <Input
                id="linear-feet"
                type="number"
                placeholder="Enter linear feet"
                value={linearFeet}
                onChange={(e) => setLinearFeet(e.target.value)}
                min="0"
                step="1"
              />
            </div>

            <div>
              <Label htmlFor="cost-per-ton" className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                Cost per Ton
              </Label>
              <Input
                id="cost-per-ton"
                type="number"
                placeholder="$0.00"
                value={costPerTon}
                onChange={(e) => setCostPerTon(e.target.value)}
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <Button onClick={handleAddPipe} className="w-full bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Piping
          </Button>
        </CardContent>
      </Card>

      {/* Totals Summary */}
      {pipingMaterials.length > 0 && (
        <Card className="bg-accent/10">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Total Linear Feet</p>
                <p className="text-2xl font-bold text-foreground">{totals.totalLinearFeet.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Weight</p>
                <p className="text-2xl font-bold text-accent">{totals.totalTons.toFixed(2)} tons</p>
                <p className="text-xs text-muted-foreground">{totals.totalWeight.toLocaleString()} lbs</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Fluid</p>
                <p className="text-2xl font-bold text-primary">{totals.totalFluidGallons.toFixed(0)} gal</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Cost</p>
                <p className="text-2xl font-bold text-green-600">${totals.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Cost/Ton</p>
                <p className="text-xl font-bold text-orange-600">
                  ${totals.totalTons > 0 ? (totals.totalCost / totals.totalTons).toFixed(2) : '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pipe List */}
      {pipingMaterials.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold">Piping List ({pipingMaterials.length})</h3>
          {pipingMaterials.map((pipe) => (
            <Card key={pipe.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Minus className="w-4 h-4 text-primary rotate-90" />
                      <h4 className="font-semibold text-foreground">{pipe.name}</h4>
                      {pipe.category === 'Custom' && <Badge variant="outline">Custom</Badge>}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-2 text-sm">
                      <div>
                        <p className="text-muted-foreground">Length</p>
                        <p className="font-medium">{pipe.linearFeet.toLocaleString()} ft</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Weight/ft</p>
                        <p className="font-medium">{pipe.weightPerFt?.toFixed(2) || '0.00'} lbs</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total Weight</p>
                        <p className="font-medium text-accent">{(pipe.totalTons || pipe.totalWeight / 2000).toFixed(2)} tons</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Fluid Capacity</p>
                        <p className="font-medium text-primary">{(pipe.totalFluidGallons || 0).toFixed(0)} gal</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Cost/Ton</p>
                        <p className="font-medium">${(pipe.costPerTon || 0).toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total Cost</p>
                        <p className="font-medium text-green-600">${(pipe.totalCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemove(pipe.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PipingCalculator;
